import type { CSSProperties, ReactNode } from "react";
import { EngineLogo } from "../components/engine-logo.js";
import {
  CalendarDays,
  FileText,
  MessageCircle,
  TrendingUp,
} from "../components/icons.js";
import { PixelArt } from "../components/pixel-art/pixel-art.js";
import { useCapturedEngines } from "../components/use-captured-engines.js";
import { Wordmark } from "../components/wordmark.js";
import "./auth.css";

/**
 * The split every signed-out screen sits in.
 *
 * Shared so the head, the column width and the panel cannot drift between
 * signing in, creating an account, "check your email", and the moment a link
 * is being exchanged for a session — four screens a person can pass through in
 * ten seconds, three of which they see for two seconds each.
 *
 * It is the onboarding wizard's split at the same ratio, deliberately. Signing
 * in hands straight over to that wizard, and the two used to be different
 * shapes — so the first thing the product did after taking your password was
 * rearrange itself.
 */
export function AuthShell({
  swap,
  aside,
  children,
}: {
  /**
   * What the right half shows instead of the four-step flow — Sign in with
   * Crux's picture of the app joining Crux — on the same art.
   */
  readonly aside?: ReactNode;
  /**
   * The other door, in the corner.
   *
   * Absent where there is no other door: the callback screen is not a choice
   * between two ways in, it is one of them happening.
   */
  readonly swap?: {
    readonly ask: string;
    readonly go: string;
    readonly onGo: () => void;
  };
  readonly children: ReactNode;
}) {
  return (
    <div className="auth">
      <div className="auth-form">
        <header className="auth-head">
          <Wordmark className="auth-wordmark" />
          {swap === undefined ? null : (
            <p className="auth-swap">
              {swap.ask}{" "}
              <button className="button-link" type="button" onClick={swap.onGo}>
                {swap.go}
              </button>
            </p>
          )}
        </header>

        <main className="auth-main">{children}</main>
      </div>

      {aside === undefined ? <AuthPanel /> : <AuthAside>{aside}</AuthAside>}
    </div>
  );
}

/**
 * The right half's frame: the sky pixel art (DESIGN.md §9, "Pixel art"),
 * edge to edge, under whatever the half holds. One frame for every
 * signed-out screen, so a person passing through four of them in ten seconds
 * never sees the picture change.
 */
function AuthAside({
  label,
  children,
}: {
  readonly label?: string;
  readonly children: ReactNode;
}) {
  return (
    <aside className="auth-aside" aria-label={label}>
      <PixelArt variant="sky" scene="spark" interactive className="auth-art" />
      {children}
    </aside>
  );
}

/**
 * What happens to a question you give us, in the order it happens.
 *
 * A flow rather than a feature list, because the four things are not four
 * things — they are one pipeline, and each step is only worth anything because
 * the one before it ran. Each maps to something in the product today: the
 * prompt library, the daily capture, the mention and sentiment pass, and the
 * citations behind every answer. Nothing here is a roadmap item wearing the
 * present tense.
 *
 * **One line each, and short.** The rail below runs on a fixed pitch so the
 * marker's stops are exact; a sentence that wrapped to a third line would push
 * its own row past the pitch every other row is on.
 */
interface FlowStep {
  /** The glyph in this step's dot. From the app's own vocabulary, not a new set. */
  readonly Icon: typeof MessageCircle;
  readonly title: string;
  readonly line: string;
  /** Whether the engine marks appear on this step's row. Exactly one of them. */
  readonly engines?: boolean;
}

const FLOW: readonly FlowStep[] = [
  {
    Icon: MessageCircle,
    title: "You choose the questions",
    line: "The ones your buyers actually type.",
  },
  {
    Icon: CalendarDays,
    title: "We ask them every day",
    line: "Every engine, the same questions.",
    engines: true,
  },
  {
    Icon: FileText,
    title: "We read every answer back",
    line: "Who is named, in what order, and how.",
  },
  {
    Icon: TrendingUp,
    title: "You see where you stand",
    line: "With the pages each answer was built from.",
  },
];

/**
 * Custom properties as a style object, narrowed in one place.
 *
 * The same helper the wizard's preview panel keeps, for the same reason: a
 * custom property is not in `CSSProperties`, and this is the one place the
 * widening happens rather than an assertion at every call site.
 */
function vars(values: Record<string, string | number>): CSSProperties {
  return values;
}

/**
 * The right half: the flow, playing one step after another.
 *
 * No testimonial, no customer logos, no star rating. Each of those would be a
 * claim we cannot support, on the one screen where a new customer has no way
 * at all to check it — the invention §0 bans, dressed as social proof.
 *
 * An ordered list, so the sequence survives the animation being off, the CSS
 * failing, or a screen reader reading it. The motion emphasises which step is
 * being described; it is never what tells you there is an order.
 */
function AuthPanel() {
  const engines = useCapturedEngines();
  return (
    <AuthAside label="What Crux does">
      <div className="auth-aside-inner">
        <p className="auth-aside-title">
          Where your brand stands when someone asks an AI instead of a search engine.
        </p>

        <ol className="auth-flow auth-flow-steps">
          {FLOW.map((step, index) => (
            <li
              className="auth-flow-step"
              key={step.title}
              style={vars({ "--i": index })}
            >
              {/* Drawn, not read. The glyph repeats what the title already
                  says, and the list is ordered for anyone hearing it. */}
              <span className="auth-flow-dot" aria-hidden="true">
                <step.Icon />
              </span>

              {/* The line down to the next step. The last one has nothing
                  below it to reach. */}
              {index < FLOW.length - 1 ? (
                <span className="auth-flow-link" aria-hidden="true" />
              ) : null}

              <div className="auth-flow-body">
                <div className="auth-flow-head">
                  <strong className="auth-flow-title">{step.title}</strong>

                  {step.engines === true ? (
                    <span className="auth-engines">
                      {/* The engines this server asks, so sign-in never promises one it does not. */}
                      {(engines ?? []).map((engine) => (
                        <EngineLogo key={engine.id} engine={engine.id} />
                      ))}
                    </span>
                  ) : null}
                </div>

                <span className="auth-flow-line">{step.line}</span>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </AuthAside>
  );
}
